import type { SupabaseClient } from '@supabase/supabase-js';
import {
  isWarehouseValue,
  newestWarehouseSnapshot,
  warehouseSendActive,
  warehouseUnresolved,
  type WarehouseSnapshot,
  type WarehouseValue
} from '../../supabase/functions/_shared/warehouse-line-contract';

interface SaveAttempt {
  requestId: string;
  value: WarehouseValue;
  phase: 'sending' | 'unknown' | 'rejected';
  error: string | null;
  retryRevision: string | null;
}

export interface WarehouseState {
  snapshot: WarehouseSnapshot | null;
  attempt: SaveAttempt | null;
  checking: boolean;
  readError: string | null;
  paused: boolean;
  saving: boolean;
}

interface Entry {
  state: WarehouseState;
  listeners: Set<() => void>;
  encompassLoanId: string;
  episodeStarted: number | null;
  sending: Promise<boolean> | null;
  acceptedRequests: Map<string, string>;
  readGeneration: number;
}

const POLL_MS = 20_000;
const EPISODE_MS = 10 * 60_000;
const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/** One store per authenticated user and organization, shared by both editors. */
export class WarehouseLineStore {
  private entries = new Map<string, Entry>();
  private requested = new Set<string>();
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private channel: ReturnType<SupabaseClient['channel']> | null = null;

  constructor(private client: SupabaseClient, private scope: string, private organizationId: string) {}

  private storageKey(loanId: string) {
    return `warehouse-intent:${this.scope}:${loanId}`;
  }

  private entry(loanId: string, encompassLoanId = ''): Entry {
    let entry = this.entries.get(loanId);
    if (!entry) {
      let attempt: SaveAttempt | null = null;
      try {
        const saved = JSON.parse(sessionStorage.getItem(this.storageKey(loanId)) ?? 'null');
        if (saved && typeof saved.requestId === 'string' && isWarehouseValue(saved.value)) {
          attempt = { requestId: saved.requestId, value: saved.value, phase: 'unknown', error: null,
            retryRevision: typeof saved.retryRevision === 'string' && /^\d+$/.test(saved.retryRevision) ? saved.retryRevision : null };
        }
      } catch { /* Storage can be unavailable in private browsing. */ }
      entry = {
        state: { snapshot: null, attempt, checking: false, readError: null, paused: false, saving: false },
        listeners: new Set(), encompassLoanId, episodeStarted: null, sending: null, acceptedRequests: new Map(), readGeneration: 0
      };
      this.entries.set(loanId, entry);
    }
    if (encompassLoanId) {
      entry.encompassLoanId = encompassLoanId;
    }
    return entry;
  }

  get = (loanId: string, encompassLoanId = '') => this.entry(loanId, encompassLoanId).state;

  private update(loanId: string, patch: Partial<WarehouseState>) {
    const entry = this.entry(loanId);
    entry.state = { ...entry.state, ...patch };
    const unresolved = warehouseUnresolved(entry.state.snapshot) || !!entry.state.attempt;
    if (unresolved && entry.episodeStarted === null) {
      entry.episodeStarted = Date.now();
    } else if (!unresolved) {
      entry.episodeStarted = null;
      entry.state.paused = false;
    }
    try {
      if (entry.state.attempt) {
        const { requestId, value, retryRevision } = entry.state.attempt;
        sessionStorage.setItem(this.storageKey(loanId), JSON.stringify({ requestId, value, retryRevision }));
      } else {
        sessionStorage.removeItem(this.storageKey(loanId));
      }
    } catch { /* In-memory receipts still work if storage is disabled. */ }
    entry.listeners.forEach(listener => listener());
  }

  accept(snapshot: WarehouseSnapshot) {
    const entry = this.entry(snapshot.loanId, snapshot.encompassLoanId);
    const newest = newestWarehouseSnapshot(entry.state.snapshot, snapshot);
    snapshot.requests.forEach(receipt => entry.acceptedRequests.set(receipt.requestId, receipt.value));
    const attempt = entry.state.attempt;
    const confirmed = attempt && (attempt.phase !== 'rejected' || attempt.retryRevision !== null)
      && this.confirmed(entry, attempt, newest);
    this.update(snapshot.loanId, { snapshot: newest, ...(confirmed ? { attempt: null } : {}) });
  }

  private confirmed(entry: Entry, attempt: SaveAttempt, snapshot = entry.state.snapshot) {
    if (attempt.retryRevision !== null) {
      // An old acceptance receipt proves the original pick, never this retry.
      return !!snapshot && snapshot.write?.requestId === attempt.requestId
        && BigInt(snapshot.revision) > BigInt(attempt.retryRevision);
    }
    return entry.acceptedRequests.get(attempt.requestId) === attempt.value;
  }

  subscribe(loanId: string, encompassLoanId: string, listener: () => void) {
    const entry = this.entry(loanId, encompassLoanId);
    entry.listeners.add(listener);
    this.scheduleRead(loanId);
    if (!this.pollTimer) {
      this.pollTimer = setInterval(() => {
        this.entries.forEach((item, id) => {
          if (!item.listeners.size || item.episodeStarted === null) {
            return;
          }
          if (Date.now() - item.episodeStarted >= EPISODE_MS) {
            if (!item.state.paused) {
              this.update(id, { paused: true });
            }
          } else {
            this.scheduleRead(id);
          }
        });
      }, POLL_MS);
      window.addEventListener('focus', this.onFocus);
      this.channel = this.client.channel(`warehouse:${this.scope}`)
        .on('postgres_changes', {
          event: 'UPDATE', schema: 'public', table: 'encompass_loans',
          filter: `organization_id=eq.${this.organizationId}`
        }, payload => {
          const id = payload.new.id as string;
          if (this.entries.get(id)?.listeners.size && !this.entry(id).state.paused) {
            this.scheduleRead(id);
          }
        }).subscribe();
    }
    return () => {
      entry.listeners.delete(listener);
      if (![...this.entries.values()].some(item => item.listeners.size)) {
        if (this.pollTimer) {
          clearInterval(this.pollTimer);
          this.pollTimer = null;
        }
        window.removeEventListener('focus', this.onFocus);
        if (this.channel) {
          void this.client.removeChannel(this.channel);
          this.channel = null;
        }
      }
    };
  }

  private onFocus = () => {
    this.entries.forEach((entry, id) => {
      if (entry.listeners.size && !entry.state.paused) {
        this.scheduleRead(id);
      }
    });
  };

  private scheduleRead(loanId: string) {
    this.requested.add(loanId);
    if (!this.flushTimer) {
      this.flushTimer = setTimeout(() => {
        this.flushTimer = null;
        const ids = [...this.requested].filter(id => this.entry(id).listeners.size);
        this.requested.clear();
        for (let start = 0; start < ids.length; start += 100) {
          void this.read(ids.slice(start, start + 100));
        }
      }, 0);
    }
  }

  invalidate(loanId: string) {
    if (this.entry(loanId).listeners.size && !this.entry(loanId).state.paused) {
      this.scheduleRead(loanId);
    }
  }

  async read(loanIds: string[]): Promise<boolean> {
    const generations = new Map(loanIds.map(id => [id, ++this.entry(id).readGeneration]));
    const finishRead = (patch: Partial<WarehouseState>) => loanIds.forEach(id => {
      if (this.entry(id).readGeneration === generations.get(id)) {
        this.update(id, patch);
      }
    });
    loanIds.forEach(id => this.update(id, { checking: true }));
    for (const delay of [0, 500, 1000, 2000]) {
      if (delay) {
        await sleep(delay);
      }
      try {
        const requestIds = loanIds.flatMap(id => {
          const requestId = this.entry(id).state.attempt?.requestId;
          return requestId ? [requestId] : [];
        });
        const query = this.client.rpc('get_warehouse_line_snapshots', {
          p_loan_ids: loanIds, p_request_ids: requestIds
        });
        query.abortSignal?.(AbortSignal.timeout(10_000));
        const { data, error } = await query;
        if (error || !Array.isArray(data) || loanIds.some(id => !data.some(item => item.loanId === id))) {
          throw new Error('Warehouse snapshot unavailable');
        }
        for (const snapshot of data as WarehouseSnapshot[]) {
          if (loanIds.includes(snapshot.loanId) && snapshot.encompassLoanId === this.entry(snapshot.loanId).encompassLoanId) {
            this.accept(snapshot);
          }
        }
        finishRead({ checking: false, readError: null });
        return true;
      } catch { /* Retry a failed read without dropping the last confirmed snapshot. */ }
    }
    finishRead({ checking: false, readError: 'Could not check warehouse status' });
    return false;
  }

  async check(loanId: string) {
    this.entry(loanId).episodeStarted = Date.now();
    this.update(loanId, { paused: false });
    return this.read([loanId]);
  }

  save(loanId: string, encompassLoanId: string, value: WarehouseValue, retry = false): Promise<boolean> {
    const entry = this.entry(loanId, encompassLoanId);
    if (entry.sending) {
      return Promise.resolve(false);
    }
    if (!isWarehouseValue(value) || warehouseSendActive(entry.state.snapshot?.write ?? null)) {
      return Promise.resolve(false);
    }
    const previous = entry.state.attempt;
    if (previous?.phase === 'unknown' && (!retry || previous.value !== value)) {
      return Promise.resolve(false);
    }
    const currentWrite = entry.state.snapshot?.write;
    const requestId = retry && previous?.value === value ? previous.requestId
      : retry && currentWrite?.value === value && currentWrite.requestId ? currentWrite.requestId : crypto.randomUUID();
    const retryRevision = retry && currentWrite?.requestId === requestId
      ? entry.state.snapshot!.revision : previous?.requestId === requestId ? previous.retryRevision : null;
    const attempt: SaveAttempt = { requestId, value, phase: 'sending', error: null, retryRevision };
    entry.episodeStarted = Date.now();
    this.update(loanId, { attempt, paused: false, saving: true });
    entry.sending = this.send(loanId, encompassLoanId, attempt).finally(() => {
      entry.sending = null;
      this.update(loanId, { saving: false });
    });
    return entry.sending;
  }

  private async send(loanId: string, encompassLoanId: string, attempt: SaveAttempt): Promise<boolean> {
    let rejected = false;
    let message = "Couldn't confirm the warehouse save";
    try {
      const result = await this.client.functions.invoke('enqueue-encompass-write', {
        body: { loanId, encompassLoanId, fieldId: 'VEND.X200', value: attempt.value, requestId: attempt.requestId },
        signal: AbortSignal.timeout(90_000)
      });
      let body = result.data;
      if (!body && result.error && 'context' in result.error && result.error.context instanceof Response) {
        body = await result.error.context.json().catch(() => null);
      }
      if (body?.snapshot?.loanId === loanId && body.snapshot.encompassLoanId === encompassLoanId) {
        this.accept(body.snapshot as WarehouseSnapshot);
      }
      rejected = body?.accepted === false;
      if (rejected && typeof body.error === 'string') {
        message = body.error;
      }
    } catch { /* A transport failure is an unknown outcome, never a rejection. */ }
    if (rejected) {
      this.update(loanId, { attempt: { ...attempt, phase: 'rejected', error: message } });
      return false;
    }
    // New picks use immutable acceptance receipts; retries need fresh progress
    // on that request, including when the HTTP reply is lost.
    const confirmed = () => this.confirmed(this.entry(loanId), attempt);
    if (!confirmed()) {
      await this.read([loanId]);
    }
    if (confirmed()) {
      this.update(loanId, { attempt: null });
      return true;
    }
    this.update(loanId, { attempt: { ...attempt, phase: 'unknown', error: message } });
    return false;
  }
}

const stores = new WeakMap<SupabaseClient, Map<string, WarehouseLineStore>>();
export function getWarehouseLineStore(client: SupabaseClient, userId: string, organizationId: string) {
  let scoped = stores.get(client);
  if (!scoped) {
    scoped = new Map();
    stores.set(client, scoped);
  }
  const key = `${userId}:${organizationId}`;
  let store = scoped.get(key);
  if (!store) {
    store = new WarehouseLineStore(client, key, organizationId);
    scoped.set(key, store);
  }
  return store;
}
